import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  Clock, 
  AlertTriangle, 
  Calendar,
  Edit,
  Target,
  RefreshCw,
  Search,
  CheckCircle,
  XCircle,
  Star,
  MessageSquare,
  Eye,
  MoreVertical,
  Filter,
  ChevronLeft,    
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  Save,
  Bookmark,
  Trash2,
  Bell,
  Building2,
  MapPin,
  DollarSign,
  Phone,
  Hash,
  Award,
  Users2,
  User,
  Mail
} from 'lucide-react';
import CertificationIcon from './CertificationIcon';
import UnifiedCandidateModal from './UnifiedCandidateModal';

// Custom Single-Select Dropdown Component
const SingleSelectDropdown = ({ label, options, value, onChange, placeholder = "Select..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-xs font-medium text-slate-700 mb-1">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-left border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white hover:bg-slate-50 transition-colors flex items-center justify-between"
      >
        <span className={!selectedOption ? 'text-slate-400' : 'text-slate-900'}>
          {displayText}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 hover:bg-slate-50 text-sm transition-colors ${
                value === option.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-900'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


// Custom Multi-Select Dropdown Component
const MultiSelectDropdown = ({ label, options, selected, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (value) => {
    const newSelected = selected.includes(value)
      ? selected.filter(item => item !== value)
      : [...selected, value];
    onChange(newSelected);
  };

  const handleSelectAll = () => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map(opt => opt.value));
    }
  };

  const handleClear = () => {
    onChange([]);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs font-medium text-slate-700 mb-1">
        {label} {selected.length > 0 && `(${selected.length})`}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-left border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white hover:bg-slate-50 transition-colors flex items-center justify-between"
      >
        <span className={selected.length === 0 ? 'text-slate-400' : 'text-slate-900'}>
          {selected.length === 0 ? placeholder : `${selected.length} selected`}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              {selected.length === options.length ? 'Deselect All' : 'Select All'}
            </button>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={handleClear}
                className="text-xs text-slate-600 hover:text-slate-800 font-medium"
              >
                Clear
              </button>
            )}
          </div>

          <div className="overflow-y-auto max-h-52">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex items-center px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() => handleToggle(option.value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                />
                <span className="text-sm text-slate-900 flex-1">{option.label}</span>
                {option.count && (
                  <span className="text-xs text-slate-500">({option.count})</span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const RollOffTab = ({
  // Data props
  rollOffCandidates,
  dataLoading,
  currentUser,
  
  // Filter states
  extensionFilter,
  setExtensionFilter,
  
  // Selection states
  selectedRollOffCandidates,
  setSelectedRollOffCandidates,
  
  // Modal states
  showRollOffMoreActions,
  setShowRollOffMoreActions,
  setSelectedCandidate,
  setShowRollOffEditModal,
  setShowCandidateDetails,
  setShowBulkUpdateModal,
  setBulkUpdateType,
  setShowExtensionNotificationModal,
  
  // Handlers
  fetchCandidates,
  getDaysRemaining,
  getExtensionPendingCandidates,

  // Unified modal
  updateCandidateInfo,
  API_BASE_URL,
}) => {
  const [rollOffSearchTerm, setRollOffSearchTerm] = useState('');
const [statusFilter, setStatusFilter] = useState('all');
  
  // Multi-select filter states
  const [clientFilter, setClientFilter] = useState([]);
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Multi-select filter states
  const [buHeadFilter, setBuHeadFilter] = useState([]);
  const [skillsFilter, setSkillsFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState([]);
  const [contractTypeFilter, setContractTypeFilter] = useState([]);
  const [rateRangeFilter, setRateRangeFilter] = useState('all');
  const [marginFilter, setMarginFilter] = useState('all');
  const [reportingStatusFilter, setReportingStatusFilter] = useState('all');
  const [projectStatusFilter, setProjectStatusFilter] = useState('all');
  const [clientProjectManagerFilter, setClientProjectManagerFilter] = useState([]);
  const [clientResourceManagerFilter, setClientResourceManagerFilter] = useState([]);
  const [reportingManagerFilter, setReportingManagerFilter] = useState([]);
  const [extensionStatusFilter, setExtensionStatusFilter] = useState([]);
  const [endDateRangeFilter, setEndDateRangeFilter] = useState('all');
  const [customEndDateStart, setCustomEndDateStart] = useState('');
  const [customEndDateEnd, setCustomEndDateEnd] = useState('');

  // Reminder states
  const [showReminderPanel, setShowReminderPanel] = useState(false);
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  const [reminderMessage, setReminderMessage] = useState('');
  const [reminderSubmitting, setReminderSubmitting] = useState(false);

  const [showUnifiedModal, setShowUnifiedModal] = useState(false);
const [unifiedModalCandidate, setUnifiedModalCandidate] = useState(null);

const handleOpenUnifiedModal = (candidate) => {
  setUnifiedModalCandidate(candidate);
  setShowUnifiedModal(true);
};

const handleSaveUnifiedModal = async (formData) => {
  if (updateCandidateInfo) {
    await updateCandidateInfo(unifiedModalCandidate.id, formData);
  }
  setShowUnifiedModal(false);
  setUnifiedModalCandidate(null);
};

  // Saved filters
  const [savedFilters, setSavedFilters] = useState([]);
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [showSavedFiltersDropdown, setShowSavedFiltersDropdown] = useState(false);
  const savedFiltersRef = useRef(null);


  // Load saved filters from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('rollOffSavedFilters');
    if (saved) {
      setSavedFilters(JSON.parse(saved));
    }
  }, []);

  // Close saved filters dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (savedFiltersRef.current && !savedFiltersRef.current.contains(event.target)) {
        setShowSavedFiltersDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get unique values for filters with counts
  const uniqueClients = useMemo(() => {
    const clientCounts = {};
    rollOffCandidates.forEach(candidate => {
      clientCounts[candidate.client] = (clientCounts[candidate.client] || 0) + 1;
    });
    return Object.keys(clientCounts)
      .sort()
      .map(client => ({
        value: client,
        label: client,
        count: clientCounts[client]
      }));
  }, [rollOffCandidates]);

  const uniqueBUHeads = useMemo(() => {
    const buHeads = [...new Set(rollOffCandidates.map(candidate => candidate.buHead))];
    return buHeads
      .filter(Boolean)
      .sort()
      .map(bu => ({ value: bu, label: bu }));
  }, [rollOffCandidates]);

  const uniqueLocations = useMemo(() => {
    const locations = [...new Set(rollOffCandidates.map(candidate => 
      `${candidate.projectCity}, ${candidate.projectState}`
    ))];
    return locations
      .filter(Boolean)
      .sort()
      .map(location => ({ value: location, label: location }));
  }, [rollOffCandidates]);

  const uniqueContractTypes = useMemo(() => {
    const types = [...new Set(rollOffCandidates.map(candidate => candidate.contractorStatus))];
    return types
      .filter(Boolean)
      .sort()
      .map(type => ({ value: type, label: type }));
  }, [rollOffCandidates]);

  const uniqueClientProjectManagers = useMemo(() => {
    const managers = [...new Set(rollOffCandidates.map(candidate => 
      candidate.clientProjectManagerName || candidate.clientProjectManager
    ))];
    return managers
      .filter(Boolean)
      .sort()
      .map(manager => ({ value: manager, label: manager }));
  }, [rollOffCandidates]);

  const uniqueClientResourceManagers = useMemo(() => {
    const managers = [...new Set(rollOffCandidates.map(candidate => 
      candidate.clientResourceManagerName || candidate.clientResourceManager
    ))];
    return managers
      .filter(Boolean)
      .sort()
      .map(manager => ({ value: manager, label: manager }));
  }, [rollOffCandidates]);

  const uniqueReportingManagers = useMemo(() => {
    const managers = [...new Set(rollOffCandidates.map(candidate => 
      candidate.reportingManagerName
    ))];
    return managers
      .filter(Boolean)
      .sort()
      .map(manager => ({ value: manager, label: manager }));
  }, [rollOffCandidates]);

  // Enhanced filter function with multi-select support
  const getFilteredRollOffCandidates = () => {
    return rollOffCandidates.filter(candidate => {
      // Search filter
      const matchesSearch = candidate.candidateName.toLowerCase().includes(rollOffSearchTerm.toLowerCase()) ||
                           candidate.client.toLowerCase().includes(rollOffSearchTerm.toLowerCase()) ||
                           candidate.jobTitle.toLowerCase().includes(rollOffSearchTerm.toLowerCase()) ||
                           candidate.candidateEmail.toLowerCase().includes(rollOffSearchTerm.toLowerCase());
      
      // Extension filter
      const extensionRequested = candidate.extension_requested ?? candidate.extensionRequested ?? null;
const reqValue = parseInt(extensionRequested);
let matchesExtension = true;

if (extensionFilter === 'Extension Request - Yes') {
  matchesExtension = reqValue === 1;
} else if (extensionFilter === 'Extension Request - No') {
  matchesExtension = reqValue === 2;
} else if (extensionFilter === 'Pending') {
  matchesExtension = !reqValue || reqValue === 0 || isNaN(reqValue) || (reqValue !== 1 && reqValue !== 2);
}

      // Multi-select filters
      const matchesClient = clientFilter.length === 0 || clientFilter.includes(candidate.client);
      const matchesBUHead = buHeadFilter.length === 0 || buHeadFilter.includes(candidate.buHead);

      // Skills filter
      const matchesSkills = !skillsFilter || 
        candidate.primarySkills.toLowerCase().includes(skillsFilter.toLowerCase());

      // Location filter
      const candidateLocation = `${candidate.projectCity}, ${candidate.projectState}`;
      const matchesLocation = locationFilter.length === 0 || locationFilter.includes(candidateLocation);

      // Contract Type filter
      const matchesContractType = contractTypeFilter.length === 0 || contractTypeFilter.includes(candidate.contractorStatus);

      // Rate Range filter
      let matchesRateRange = true;
      if (rateRangeFilter !== 'all') {
        const payRate = parseFloat(candidate.payRate) || 0;
        switch (rateRangeFilter) {
          case 'low':
            matchesRateRange = payRate < 100;
            break;
          case 'medium':
            matchesRateRange = payRate >= 100 && payRate <= 200;
            break;
          case 'high':
            matchesRateRange = payRate > 200;
            break;
        }
      }

      // Margin filter
      let matchesMargin = true;
      if (marginFilter !== 'all') {
        const margin = parseFloat(candidate.marginValue) || 0;
        switch (marginFilter) {
          case 'low':
            matchesMargin = margin < 20;
            break;
          case 'medium':
            matchesMargin = margin >= 20 && margin <= 35;
            break;
          case 'high':
            matchesMargin = margin > 35;
            break;
        }
      }

      // Multi-select manager filters
      const matchesClientPM = clientProjectManagerFilter.length === 0 || 
        clientProjectManagerFilter.includes(candidate.clientProjectManagerName || candidate.clientProjectManager);

      const matchesClientRM = clientResourceManagerFilter.length === 0 || 
        clientResourceManagerFilter.includes(candidate.clientResourceManagerName || candidate.clientResourceManager);

      const matchesReportingManager = reportingManagerFilter.length === 0 || 
        reportingManagerFilter.includes(candidate.reportingManagerName);

      // Extension Status filter
      let matchesExtensionStatus = true;
if (Array.isArray(extensionStatusFilter) && extensionStatusFilter.length > 0) {
  const reqVal = parseInt(candidate.extension_requested ?? candidate.extensionRequested ?? 0);
  matchesExtensionStatus = extensionStatusFilter.some((filter) => {
    if (filter === 'Extension Request - Yes') return reqVal === 1;
    if (filter === 'Extension Request - No') return reqVal === 2;
    if (filter === 'Pending') return !reqVal || reqVal === 0 || isNaN(reqVal) || (reqVal !== 1 && reqVal !== 2);
    return false;
  });
}

      // Reporting Status filter
      let matchesReportingStatus = true;
      if (reportingStatusFilter !== 'all') {
        const hasReporting = candidate.workEmail || candidate.reportingManagerName || candidate.reportingManagerEmail;
        switch (reportingStatusFilter) {
          case 'completed':
            matchesReportingStatus = hasReporting;
            break;
          case 'pending':
            matchesReportingStatus = !hasReporting;
            break;
        }
      }

      // Project Status filter
      let matchesProjectStatus = true;
      if (projectStatusFilter !== 'all') {
        const hasProject = candidate.current_technologies || candidate.project_description || candidate.project_experience_rating;
        switch (projectStatusFilter) {
          case 'completed':
            matchesProjectStatus = hasProject;
            break;
          case 'pending':
            matchesProjectStatus = !hasProject;
            break;
        }
      }

      // End Date Range filter
      const matchesEndDateRange = true;

      // Date range filter
      let matchesDateRange = true;
      if (dateRangeFilter !== 'all' && candidate.sowEndDate) {
        const sowEndDate = new Date(candidate.sowEndDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const days = Math.ceil((sowEndDate - today) / (1000 * 60 * 60 * 24));

        switch (dateRangeFilter) {
          case 'overdue':
            matchesDateRange = days < 0;
            break;
          case 'critical': // ≤7 days
            matchesDateRange = days >= 0 && days <= 7;
            break;
          case 'warning': // 8–30 days
            matchesDateRange = days >= 0 && days <= 30;
            break;
          case 'upcoming': // 31–60 days
            matchesDateRange = days >= 0 && days <= 60;
            break;
          case 'future': // 60+ days
            matchesDateRange = days > 60;
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              const startDate = new Date(customStartDate);
              startDate.setHours(0, 0, 0, 0);
              const endDate = new Date(customEndDate);
              endDate.setHours(23, 59, 59, 999);
              matchesDateRange = sowEndDate >= startDate && sowEndDate <= endDate;
            }
            break;
          default:
            matchesDateRange = true;
        }
      }
      
      // Add this line above the return
const matchesStatus = statusFilter === 'all' || 
  (candidate.status || '').toLowerCase() === statusFilter.toLowerCase();

return matchesStatus && matchesSearch && matchesExtension && matchesClient && matchesBUHead && 
             matchesSkills && matchesLocation && matchesContractType && matchesRateRange && 
             matchesMargin && matchesClientPM && matchesClientRM && matchesReportingManager && 
             matchesExtensionStatus && matchesReportingStatus && matchesProjectStatus && 
             matchesDateRange;
    });
  };

  const filteredRollOffCandidates = getFilteredRollOffCandidates();

  // Pagination logic
  const totalPages = Math.ceil(filteredRollOffCandidates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRollOffCandidates = filteredRollOffCandidates.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [rollOffSearchTerm, extensionFilter, clientFilter, dateRangeFilter, buHeadFilter, skillsFilter, locationFilter, contractTypeFilter, rateRangeFilter, marginFilter, reportingStatusFilter, projectStatusFilter, clientProjectManagerFilter, clientResourceManagerFilter, reportingManagerFilter, extensionStatusFilter, endDateRangeFilter]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const critical = rollOffCandidates.filter(c => {
      if (!c.sowEndDate) return false;
      const days = Math.ceil((new Date(c.sowEndDate) - new Date()) / (1000 * 60 * 60 * 24));
      return days <= 7 && days >= 0;
    }).length;

    const warning = rollOffCandidates.filter(c => {
      if (!c.sowEndDate) return false;
      const days = Math.ceil((new Date(c.sowEndDate) - new Date()) / (1000 * 60 * 60 * 24));
      return days <= 30 && days > 7;
    }).length;

    const upcoming = rollOffCandidates.filter(c => {
      if (!c.sowEndDate) return false;
      const days = Math.ceil((new Date(c.sowEndDate) - new Date()) / (1000 * 60 * 60 * 24));
      return days > 30 && days <= 60;
    }).length;

    const extended = rollOffCandidates.filter(c => c.extension_status === 'Yes').length;

    const overdue = rollOffCandidates.filter(c => {
      if (!c.sowEndDate) return false;
      const days = Math.ceil((new Date(c.sowEndDate) - new Date()) / (1000 * 60 * 60 * 24));
      return days < 0;
    }).length;

    const pending = rollOffCandidates.filter(c => {
  const reqVal = parseInt(c.extension_requested ?? c.extensionRequested ?? 0);
  return !reqVal || reqVal === 0 || isNaN(reqVal) || (reqVal !== 1 && reqVal !== 2);
}).length;

    return { critical, warning, upcoming, extended, overdue, pending };
  }, [rollOffCandidates]);

  const getUrgencyColor = (days) => {
    if (days < 0) return 'border-purple-200';
    if (days <= 7) return 'border-red-200';
    if (days <= 30) return 'border-yellow-200';
    return 'border-slate-200';
  };
  
  const getUrgencyBadge = (days) => {
    if (days < 0) return 'bg-purple-100 text-purple-800 border-purple-200';
    if (days <= 7) return 'bg-red-100 text-red-800 border-red-200';
    if (days <= 30) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  // Clear all filters
  const clearAllFilters = () => {
  setRollOffSearchTerm('');
  setExtensionFilter('all');
  setStatusFilter('all');
  setClientFilter([]);
    setDateRangeFilter('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setBuHeadFilter([]);
    setSkillsFilter('');
    setLocationFilter([]);
    setContractTypeFilter([]);
    setRateRangeFilter('all');
    setMarginFilter('all');
    setReportingStatusFilter('all');
    setProjectStatusFilter('all');
    setClientProjectManagerFilter([]);
    setClientResourceManagerFilter([]);
    setReportingManagerFilter([]);
    setExtensionStatusFilter([]);
    setEndDateRangeFilter('all');
    setCustomEndDateStart('');
    setCustomEndDateEnd('');
    setSelectedRollOffCandidates([]);
    setCurrentPage(1);
  };

  // Get active filters count
  const getActiveFiltersCount = () => {
    let count = 0;
if (rollOffSearchTerm) count++;
if (extensionFilter !== 'all') count++;
if (statusFilter !== 'all') count++;
    if (clientFilter.length > 0) count++;
    if (dateRangeFilter !== 'all') count++;
    if (buHeadFilter.length > 0) count++;
    if (skillsFilter) count++;
    if (locationFilter.length > 0) count++;
    if (contractTypeFilter.length > 0) count++;
    if (rateRangeFilter !== 'all') count++;
    if (marginFilter !== 'all') count++;
    if (reportingStatusFilter !== 'all') count++;
    if (projectStatusFilter !== 'all') count++;
    if (clientProjectManagerFilter.length > 0) count++;
    if (clientResourceManagerFilter.length > 0) count++;
    if (reportingManagerFilter.length > 0) count++;
    if (extensionStatusFilter.length > 0) count++;
    if (endDateRangeFilter !== 'all') count++;
    return count;
  };

  // Save current filter
  const saveCurrentFilter = () => {
    if (!filterName.trim()) {
      alert('Please enter a filter name');
      return;
    }

    const filterConfig = {
      id: Date.now(),
      name: filterName,
      filters: {
        rollOffSearchTerm,
        extensionFilter,
        clientFilter,
        dateRangeFilter,
        customStartDate,
        customEndDate,
        buHeadFilter,
        skillsFilter,
        locationFilter,
        contractTypeFilter,
        rateRangeFilter,
        marginFilter,
        reportingStatusFilter,
        projectStatusFilter,
        clientProjectManagerFilter,
        clientResourceManagerFilter,
        reportingManagerFilter,
        extensionStatusFilter: extensionStatusFilter,
        endDateRangeFilter,
        customEndDateStart,
        customEndDateEnd
      }
    };

    const updatedFilters = [...savedFilters, filterConfig];
    setSavedFilters(updatedFilters);
    localStorage.setItem('rollOffSavedFilters', JSON.stringify(updatedFilters));
    setFilterName('');
    setShowSaveFilterModal(false);
    alert('Filter saved successfully!');
  };

  // Load saved filter
  const loadSavedFilter = (filter) => {
    const f = filter.filters;
    setRollOffSearchTerm(f.rollOffSearchTerm || '');
    setExtensionFilter(f.extensionFilter || 'all');
    setClientFilter(f.clientFilter || []);
    setDateRangeFilter(f.dateRangeFilter || 'all');
    setCustomStartDate(f.customStartDate || '');
    setCustomEndDate(f.customEndDate || '');
    setBuHeadFilter(f.buHeadFilter || []);
    setSkillsFilter(f.skillsFilter || '');
    setLocationFilter(f.locationFilter || []);
    setContractTypeFilter(f.contractTypeFilter || []);
    setRateRangeFilter(f.rateRangeFilter || 'all');
    setMarginFilter(f.marginFilter || 'all');
    setReportingStatusFilter(f.reportingStatusFilter || 'all');
    setProjectStatusFilter(f.projectStatusFilter || 'all');
    setClientProjectManagerFilter(f.clientProjectManagerFilter || []);
    setClientResourceManagerFilter(f.clientResourceManagerFilter || []);
    setReportingManagerFilter(f.reportingManagerFilter || []);
    setExtensionStatusFilter(Array.isArray(f.extensionStatusFilter) ? f.extensionStatusFilter : []);
    setEndDateRangeFilter(f.endDateRangeFilter || 'all');
    setCustomEndDateStart(f.customEndDateStart || '');
    setCustomEndDateEnd(f.customEndDateEnd || '');
    setShowSavedFiltersDropdown(false);
  };

  // Delete saved filter
  const deleteSavedFilter = (filterId) => {
    if (window.confirm('Are you sure you want to delete this saved filter?')) {
      const updatedFilters = savedFilters.filter(f => f.id !== filterId);
      setSavedFilters(updatedFilters);
      localStorage.setItem('rollOffSavedFilters', JSON.stringify(updatedFilters));
    }
  };

  const handleSetReminder = async () => {
    if (!reminderDate || !reminderTime || !reminderMessage.trim()) {
      alert('Please fill all reminder fields');
      return;
    }

    setReminderSubmitting(true);
    
    try {
      const reminderDateTime = `${reminderDate} ${reminderTime}`;
      
      const response = await fetch('https://locallhost/api/reminders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_email: currentUser?.email,
          user_name: currentUser?.name,
          reminder_datetime: reminderDateTime,
          message: reminderMessage,
          created_by: currentUser?.name
        })
      });

      if (response.ok) {
        alert('Reminder set successfully! You will receive an email at the specified time.');
        setReminderDate('');
        setReminderTime('');
        setReminderMessage('');
        setShowReminderPanel(false);
      } else {
        alert('Failed to set reminder. Please try again.');
      }
    } catch (error) {
      console.error('Error setting reminder:', error);
      alert('Error setting reminder. Please try again.');
    } finally {
      setReminderSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 px-2 sm:px-4 lg:px-0">
      {/* Compact Metrics Cards */}
      <div className="space-y-3">

  {/* Row 1 — always 3 equal columns */}
  <div className="grid grid-cols-3 gap-3">

    {/* Total Roll-off */}
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-4 rounded-xl shadow-sm border border-slate-700">
      <div className="flex items-center space-x-2 mb-2">
        <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
          <Users2 className="w-4 h-4 text-white" />
        </div>
        <p className="text-xs font-semibold text-slate-300">Total Roll-off</p>
      </div>
      <div className="text-3xl font-bold text-white">{rollOffCandidates.length}</div>
      <p className="text-xs text-slate-400 mt-1">Active candidates rolling off</p>
    </div>

    {/* Extension Requested */}
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl shadow-sm border border-green-200">
      <div className="flex items-center space-x-2 mb-2">
        <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
          <CheckCircle className="w-4 h-4 text-white" />
        </div>
        <p className="text-xs font-semibold text-green-700">Ext Requested</p>
      </div>
      <div className="text-3xl font-bold text-slate-900">
        {rollOffCandidates.filter(c => parseInt(c.extension_requested) === 1).length}
      </div>
      <p className="text-xs text-green-600 mt-1">Pending contracts action</p>
    </div>

    {/* Not Extending */}
    <div className="bg-gradient-to-br from-red-50 to-pink-50 p-4 rounded-xl shadow-sm border border-red-200">
      <div className="flex items-center space-x-2 mb-2">
        <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg flex items-center justify-center">
          <XCircle className="w-4 h-4 text-white" />
        </div>
        <p className="text-xs font-semibold text-red-700">Not Extending</p>
      </div>
      <div className="text-3xl font-bold text-slate-900">
        {rollOffCandidates.filter(c => parseInt(c.extension_requested) === 2).length}
      </div>
      <p className="text-xs text-red-600 mt-1">Pending contracts action</p>
    </div>

  </div>

  {/* Divider */}
  <div className="border-t border-slate-200" />

  {/* Row 2 — 4 urgency cards */}
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">

    {/* Overdue */}
    <div className="bg-white p-3 rounded-xl shadow-sm border border-purple-200">
      <div className="flex items-center space-x-2 mb-2">
        <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-violet-500 rounded-lg flex items-center justify-center">
          <AlertTriangle className="w-4 h-4 text-white" />
        </div>
        <p className="text-xs font-semibold text-purple-600">Overdue</p>
      </div>
      <div className="text-2xl font-bold text-slate-900">{metrics.overdue}</div>
    </div>

    {/* Critical */}
    <div className="bg-white p-3 rounded-xl shadow-sm border border-red-200">
      <div className="flex items-center space-x-2 mb-2">
        <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg flex items-center justify-center">
          <AlertTriangle className="w-4 h-4 text-white" />
        </div>
        <p className="text-xs font-semibold text-red-600">7 Days</p>
      </div>
      <div className="text-2xl font-bold text-slate-900">{metrics.critical}</div>
    </div>

    {/* Warning */}
    <div className="bg-white p-3 rounded-xl shadow-sm border border-yellow-200">
      <div className="flex items-center space-x-2 mb-2">
        <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
          <Clock className="w-4 h-4 text-white" />
        </div>
        <p className="text-xs font-semibold text-yellow-600">30 Days</p>
      </div>
      <div className="text-2xl font-bold text-slate-900">{metrics.warning}</div>
    </div>

    {/* Upcoming */}
    <div className="bg-white p-3 rounded-xl shadow-sm border border-blue-200">
      <div className="flex items-center space-x-2 mb-2">
        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
          <Calendar className="w-4 h-4 text-white" />
        </div>
        <p className="text-xs font-semibold text-blue-600">60 Days</p>
      </div>
      <div className="text-2xl font-bold text-slate-900">{metrics.upcoming}</div>
    </div>

  </div>

</div>

      {/* Compact Filters Header */}
      <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-4 border border-red-200">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Roll-off Candidates</h3>
                <p className="text-xs text-slate-600">
                  {paginatedRollOffCandidates.length} of {filteredRollOffCandidates.length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search..."
                value={rollOffSearchTerm}
                onChange={(e) => setRollOffSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 text-sm"
              />
            </div>
            
            <div className="flex gap-2">
              <button 
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center px-3 py-2 border rounded-lg text-sm transition-colors ${
                  showAdvancedFilters ? 'bg-blue-50 text-blue-700 border-blue-300' : 'text-slate-600 border-slate-300'
                }`}
              >
                <Filter className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">Filters</span>
                {getActiveFiltersCount() > 0 && (
                  <span className="ml-1.5 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {getActiveFiltersCount()}
                  </span>
                )}
              </button>

              {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
                <button 
                  onClick={() => {
                    const pendingCandidates = getExtensionPendingCandidates();
                    if (pendingCandidates.length === 0) {
                      alert('No candidates require extension notification.');
                      return;
                    }
                    setShowExtensionNotificationModal(true);
                  }}
                  className="flex items-center px-3 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 text-sm font-medium"
                >
                  <Bell className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">Notify</span>
                </button>
              )}

              {/* <button 
                onClick={() => setShowReminderPanel(!showReminderPanel)}
                className="flex items-center px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 text-sm font-medium"
              >
                <Bell className="w-4 h-4 mr-1.5" />
                <span className="hidden xs:inline">Reminder</span>
              </button> */}

              <div className="relative" ref={savedFiltersRef}>
                <button 
                  onClick={() => setShowSavedFiltersDropdown(!showSavedFiltersDropdown)}
                  className="flex items-center px-3 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-white text-sm"
                >
                  <Bookmark className="w-4 h-4 mr-1.5" />
                  {savedFilters.length > 0 && (
                    <span className="ml-1 bg-slate-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {savedFilters.length}
                    </span>
                  )}
                </button>

                {showSavedFiltersDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 z-30 max-h-80 overflow-y-auto">
                    <div className="p-3 border-b border-slate-200 bg-slate-50">
                      <h4 className="text-sm font-semibold text-slate-900">Saved Filters</h4>
                    </div>
                    {savedFilters.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No saved filters
                      </div>
                    ) : (
                      <div className="py-2">
                        {savedFilters.map((filter) => (
                          <div key={filter.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                            <button
                              onClick={() => loadSavedFilter(filter)}
                              className="flex-1 text-left text-sm text-slate-700 hover:text-slate-900"
                            >
                              {filter.name}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteSavedFilter(filter.id);
                              }}
                              className="ml-2 p-1 text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {getActiveFiltersCount() > 0 && (
                <button 
                  onClick={() => setShowSaveFilterModal(true)}
                  className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <Save className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">Save</span>
                </button>
              )}
              
              <button 
                onClick={fetchCandidates}
                className="flex items-center px-3 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-white text-sm"
                disabled={dataLoading}
              >
                <RefreshCw className={`w-4 h-4 ${dataLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Quick Filters */}
<div className="px-0 py-3 mt-2" style={{ overflow: 'visible' }}>
  <div className="flex flex-col gap-3">

    {/* Row 1 — Dropdowns */}
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-24 shrink-0">Quick Filters</span>
      <div style={{ minWidth: '140px' }}>
        <SingleSelectDropdown
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' }
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Status"
        />
      </div>
      <div style={{ minWidth: '140px' }}>
        <MultiSelectDropdown
          options={uniqueClients}
          selected={clientFilter}
          onChange={setClientFilter}
          placeholder="Clients"
        />
      </div>
      <div style={{ minWidth: '140px' }}>
        <MultiSelectDropdown
          options={uniqueBUHeads}
          selected={buHeadFilter}
          onChange={setBuHeadFilter}
          placeholder="Business Units"
        />
      </div>
      {getActiveFiltersCount() > 0 && (
        <button
          onClick={clearAllFilters}
          className="flex items-center px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-xs font-medium ml-auto"
        >
          <X className="w-3 h-3 mr-1" />
          Clear All
        </button>
      )}
    </div>

    {/* Divider */}
    <div className="border-t border-slate-200" />

    {/* Row 2 — Extension pills */}
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-24 shrink-0">Extension</span>
      <button
        onClick={() => setExtensionFilter('all')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          extensionFilter === 'all'
            ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
        }`}
      >
        All ({rollOffCandidates.length})
      </button>
      <button
        onClick={() => setExtensionFilter('Extension Request - Yes')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          extensionFilter === 'Extension Request - Yes'
            ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
        }`}
      >
        <CheckCircle className="w-3 h-3 inline mr-1" />
        Extending · Pending Contracts ({rollOffCandidates.filter(c => parseInt(c.extension_requested) === 1).length})
      </button>
      <button
        onClick={() => setExtensionFilter('Extension Request - No')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          extensionFilter === 'Extension Request - No'
            ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white'
            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
        }`}
      >
        <XCircle className="w-3 h-3 inline mr-1" />
        Not Extending · Pending Contracts ({rollOffCandidates.filter(c => parseInt(c.extension_requested) === 2).length})
      </button>
      <button
        onClick={() => setExtensionFilter('Pending')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          extensionFilter === 'Pending'
            ? 'bg-gradient-to-r from-slate-500 to-gray-500 text-white'
            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
        }`}
      >
        <Clock className="w-3 h-3 inline mr-1" />
        Awaiting Decision ({metrics.pending})
      </button>
    </div>

    {/* Divider */}
    <div className="border-t border-slate-200" />

    {/* Row 3 — Date range pills */}
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-24 shrink-0">Roll-off In</span>
      <button
        onClick={() => setDateRangeFilter(dateRangeFilter === 'overdue' ? 'all' : 'overdue')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          dateRangeFilter === 'overdue'
            ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white'
            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
        }`}
      >
        <AlertTriangle className="w-3 h-3 inline mr-1" />
        Overdue ({metrics.overdue})
      </button>
      <button
        onClick={() => setDateRangeFilter(dateRangeFilter === 'critical' ? 'all' : 'critical')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          dateRangeFilter === 'critical'
            ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white'
            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
        }`}
      >
        <Clock className="w-3 h-3 inline mr-1" />
        Within 7 Days ({metrics.critical})
      </button>
      <button
        onClick={() => setDateRangeFilter(dateRangeFilter === 'warning' ? 'all' : 'warning')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          dateRangeFilter === 'warning'
            ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
        }`}
      >
        <Clock className="w-3 h-3 inline mr-1" />
        Within 30 Days ({metrics.warning})
      </button>
      <button
        onClick={() => setDateRangeFilter(dateRangeFilter === 'upcoming' ? 'all' : 'upcoming')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          dateRangeFilter === 'upcoming'
            ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
        }`}
      >
        <Calendar className="w-3 h-3 inline mr-1" />
        Within 60 Days ({metrics.upcoming})
      </button>
    </div>

  </div>
</div>

        {/* Advanced Filters Panel — matches CandidatesTab UI */}
        {showAdvancedFilters && (
          <div className="border-t border-slate-200 bg-slate-50/30 overflow-visible mt-3 rounded-xl">
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-visible">

              {/* Client PM */}
              <div className="relative">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Client PM
                </label>
                <MultiSelectDropdown
                  options={uniqueClientProjectManagers}
                  selected={clientProjectManagerFilter}
                  onChange={setClientProjectManagerFilter}
                  placeholder="All"
                />
              </div>

              {/* Client RM */}
              <div className="relative">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Client RM
                </label>
                <MultiSelectDropdown
                  options={uniqueClientResourceManagers}
                  selected={clientResourceManagerFilter}
                  onChange={setClientResourceManagerFilter}
                  placeholder="All"
                />
              </div>

              {/* Reporting Manager */}
              <div className="relative">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Reporting Mgr
                </label>
                <MultiSelectDropdown
                  options={uniqueReportingManagers}
                  selected={reportingManagerFilter}
                  onChange={setReportingManagerFilter}
                  placeholder="All"
                />
              </div>

              {/* Approval Request */}
<div className="relative">
  <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
    <span className="w-1 h-1 rounded-full bg-blue-500"></span>
    Approval Request {extensionStatusFilter.length > 0 && `(${extensionStatusFilter.length})`}
  </label>
  <MultiSelectDropdown
    options={[
      { value: 'Extension Request - Yes', label: 'Extension Request - Yes' },
      { value: 'Extension Request - No', label: 'Extension Request - No' },
      { value: 'Pending', label: 'Pending' }
    ]}
    selected={extensionStatusFilter}
    onChange={setExtensionStatusFilter}
    placeholder="All"
  />
</div>

              {/* Skills */}
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Skills
                </label>
                <input
                  type="text"
                  placeholder="Search..."
                  value={skillsFilter}
                  onChange={(e) => setSkillsFilter(e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Location */}
              <div className="relative">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Location
                </label>
                <MultiSelectDropdown
                  options={uniqueLocations}
                  selected={locationFilter}
                  onChange={setLocationFilter}
                  placeholder="All"
                />
              </div>

              {/* Contract Type */}
              <div className="relative">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Contract Type
                </label>
                <MultiSelectDropdown
                  options={uniqueContractTypes}
                  selected={contractTypeFilter}
                  onChange={setContractTypeFilter}
                  placeholder="All"
                />
              </div>

              {/* Rate Range */}
              <div className="relative">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Rate Range
                </label>
                <SingleSelectDropdown
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'low', label: '<$100/hr' },
                    { value: 'medium', label: '$100-$200/hr' },
                    { value: 'high', label: '$200+/hr' }
                  ]}
                  value={rateRangeFilter}
                  onChange={setRateRangeFilter}
                  placeholder="All"
                />
              </div>

              {/* Margin */}
              <div className="relative">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Margin
                </label>
                <SingleSelectDropdown
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'low', label: '<20%' },
                    { value: 'medium', label: '20-35%' },
                    { value: 'high', label: '35%+' }
                  ]}
                  value={marginFilter}
                  onChange={setMarginFilter}
                  placeholder="All"
                />
              </div>

              {/* Reporting Status */}
              <div className="relative">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Reporting
                </label>
                <SingleSelectDropdown
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'pending', label: 'Pending' }
                  ]}
                  value={reportingStatusFilter}
                  onChange={setReportingStatusFilter}
                  placeholder="All"
                />
              </div>

              {/* Project Status */}
              <div className="relative">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Project
                </label>
                <SingleSelectDropdown
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'pending', label: 'Pending' }
                  ]}
                  value={projectStatusFilter}
                  onChange={setProjectStatusFilter}
                  placeholder="All"
                />
              </div>

              {/* SOW End Date */}
              <div className="relative">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Contract End
                </label>
                <SingleSelectDropdown
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'overdue', label: `Overdue (${metrics.overdue})` },
                    { value: 'critical', label: `≤7 days (${metrics.critical})` },
                    { value: 'warning', label: `8-30 days (${metrics.warning})` },
                    { value: 'upcoming', label: `31-60 days (${metrics.upcoming})` },
                    { value: 'future', label: '60+ days' },
                    { value: 'custom', label: 'Custom Range' }
                  ]}
                  value={dateRangeFilter}
                  onChange={setDateRangeFilter}
                  placeholder="All"
                />
              </div>

            </div>

            {/* Custom Date Range */}
            {dateRangeFilter === 'custom' && (
              <div className="px-5 pb-3">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-slate-600 mb-1">From Date</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-slate-600 mb-1">To Date</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="px-5 pb-3 flex items-center justify-between">
              <span className="text-[10px] text-slate-600">
                <span className="font-semibold text-blue-600">{filteredRollOffCandidates.length}</span> matches
              </span>
              <button
                onClick={clearAllFilters}
                className="px-2 py-1 text-[10px] text-slate-600 hover:text-slate-900 hover:bg-white rounded-lg transition-colors"
              >
                Reset all filters
              </button>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        {selectedRollOffCandidates.length > 0 && (currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
          <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-200 mt-3">
            <span className="text-xs text-slate-600">
              {selectedRollOffCandidates.length} selected
            </span>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setBulkUpdateType('extend');
                  setShowBulkUpdateModal(true);
                }}
                className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Extend
              </button>
              
              <button
                onClick={() => {
                  setBulkUpdateType('end');
                  setShowBulkUpdateModal(true);
                }}
                className="flex items-center px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs"
              >
                <XCircle className="w-3 h-3 mr-1" />
                End
              </button>
              
              <button
                onClick={() => setSelectedRollOffCandidates([])}
                className="text-slate-600 hover:text-slate-800 text-xs"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Personal Reminder Modal */}
      {showReminderPanel && (
        <>
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 animate-fadeIn"
            onClick={() => setShowReminderPanel(false)}
          />
          
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto animate-slideUp border-2 border-amber-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-5 rounded-t-2xl relative overflow-hidden">
                <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                      <Bell className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white">Set Reminder</h4>
                      <p className="text-xs text-white/90">Get email notification</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowReminderPanel(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700 flex items-center">
                    <Calendar className="w-3.5 h-3.5 mr-1.5 text-amber-600" />
                    Date
                  </label>
                  <input
                    type="date"
                    value={reminderDate}
                    onChange={(e) => setReminderDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700 flex items-center">
                    <Clock className="w-3.5 h-3.5 mr-1.5 text-amber-600" />
                    Time
                  </label>
                  <input
                    type="time"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700 flex items-center">
                    <MessageSquare className="w-3.5 h-3.5 mr-1.5 text-amber-600" />
                    Message
                  </label>
                  <textarea
                    value={reminderMessage}
                    onChange={(e) => setReminderMessage(e.target.value)}
                    placeholder="What to remind?"
                    rows="3"
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm resize-none"
                    required
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <p className="text-xs text-slate-600 flex items-center">
                    <Mail className="w-3 h-3 mr-1 text-amber-600" />
                    To: <span className="font-semibold ml-1">{currentUser?.email}</span>
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-b-2xl flex items-center justify-end space-x-2 border-t">
                <button
                  onClick={() => {
                    setReminderDate('');
                    setReminderTime('');
                    setReminderMessage('');
                    setShowReminderPanel(false);
                  }}
                  className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-white transition-all text-sm"
                  disabled={reminderSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetReminder}
                  disabled={reminderSubmitting || !reminderDate || !reminderTime || !reminderMessage.trim()}
                  className="px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {reminderSubmitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Setting...</span>
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4" />
                      <span>Set</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Save Filter Modal */}
      {showSaveFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Save Filter</h3>
            <p className="text-sm text-slate-600 mb-4">
              Name this filter to quickly apply it later.
            </p>
            <input
              type="text"
              placeholder="Enter filter name..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm mb-6"
              autoFocus
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSaveFilterModal(false);
                  setFilterName('');
                }}
                className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={saveCurrentFilter}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Cards - Matching Candidates Tab Design */}
<div className="space-y-3 sm:space-y-4">
        {dataLoading ? (
          <div className="bg-white rounded-xl p-8 text-center border border-slate-200">
            <RefreshCw className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-4" />
            <p className="text-sm text-slate-500">Loading...</p>
          </div>
        ) : filteredRollOffCandidates.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-slate-200">
            <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No candidates found</h3>
            <p className="text-sm text-slate-500 mb-4">No matches for current filters.</p>
            {getActiveFiltersCount() > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          paginatedRollOffCandidates.map((candidate) => {
            const daysRemaining = getDaysRemaining(candidate);
            const isSelected = selectedRollOffCandidates.includes(candidate.id);
            
            return (
  <div key={candidate.id} className={`rounded-xl sm:rounded-2xl shadow-sm border transition-all duration-200 ${
    isSelected 
      ? 'border-blue-500 bg-blue-50/50' 
      : 'bg-white border-slate-200 hover:shadow-lg hover:border-slate-300'
  }`}>
    <div className="p-4 sm:p-6">
      {/* Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 break-words">
              {candidate.candidateName}
            </h3>
            
            <span className={`px-3 py-1 text-xs sm:text-sm font-semibold rounded-full inline-block ${getUrgencyBadge(daysRemaining)}`}>
              {daysRemaining < 0 ? `${Math.abs(daysRemaining)}d overdue` : 
               daysRemaining === 0 ? 'Today' : 
               `${daysRemaining}d left`}
            </span>

            {parseInt(candidate.extension_requested) === 1 && (
  <span className="px-3 py-1 text-xs sm:text-sm font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-300 inline-block">
    ✓ Extending · Pending Contracts
  </span>
)}
{parseInt(candidate.extension_requested) === 2 && (
  <span className="px-3 py-1 text-xs sm:text-sm font-semibold rounded-full bg-red-100 text-red-800 border border-red-200 inline-block">
    ✗ Not Extending · Pending Contracts
  </span>
)}
{(!candidate.extension_requested || parseInt(candidate.extension_requested) === 0) && (
  <span className="px-3 py-1 text-xs sm:text-sm font-semibold rounded-full bg-slate-100 text-slate-600 border border-slate-200 inline-block">
    ⏳ Awaiting Decision
  </span>
)}

            {candidate.clientRating && parseFloat(candidate.clientRating) > 0 && (
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                <span className="text-sm font-bold text-slate-900">{candidate.clientRating}/5</span>
              </div>
            )}

            
          </div>
          
          <p className="font-medium text-sm sm:text-base text-slate-600 break-words">
            {candidate.jobTitle}
          </p>
          <p className="text-xs sm:text-sm text-slate-500">{candidate.plcCode} • {candidate.sapId}</p>

          {/* <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-slate-500 mt-2">
            <span className="flex items-center">
              <Building2 className="w-3 h-3 mr-1" />
              {candidate.client}
            </span>
            <span className="flex items-center">
              <MapPin className="w-3 h-3 mr-1" />
              {candidate.projectCity}, {candidate.projectState}
            </span>
            <span className="flex items-center">
              <Calendar className="w-3 h-3 mr-1" />
              {new Date(candidate.sowEndDate).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
              })}
            </span>
          </div> */}
        </div>

        {/* Action Buttons - Top Right */}
        <div className="flex items-center gap-2">
          {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
            <label className="flex items-center" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRollOffCandidates(prev => [...prev, candidate.id]);
                  } else {
                    setSelectedRollOffCandidates(prev => prev.filter(id => id !== candidate.id));
                  }
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </label>
          )}
          
          <button 
  onClick={() => handleOpenUnifiedModal(candidate)}
  className="flex items-center px-3 sm:px-4 py-2 sm:py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-600 rounded-xl hover:from-purple-600 hover:via-blue-600 hover:to-indigo-700 shadow-lg shadow-purple-500/30 hover:shadow-xl transition-all duration-300 hover:scale-105"
  title="Update All Information"
>
  <Edit className="w-4 h-4 mr-2" />
  <span>Update Info</span>
</button>

<button 
  onClick={() => {
    setSelectedCandidate(candidate);
    setShowRollOffEditModal(true);
  }}
  className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
>
            <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
            <span className="hidden sm:inline">Manage</span>
            <span className="sm:hidden">Roll-off</span>
          </button>
          
          <div className="relative">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowRollOffMoreActions(showRollOffMoreActions === candidate.id ? null : candidate.id);
              }}
              className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            {showRollOffMoreActions === candidate.id && (
              <div className="absolute right-0 top-full mt-2 w-48 sm:w-56 bg-white rounded-xl shadow-xl border border-slate-200 z-20">
                <div className="py-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCandidate(candidate);
                      setShowCandidateDetails(true);
                      setShowRollOffMoreActions(null);
                    }}
                    className="flex items-center w-full px-4 py-2 sm:py-3 text-xs sm:text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 sm:mr-3" />
                    View Full Details
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="space-y-1">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Client & Track</p>
          <p className="text-sm font-bold text-slate-900 break-words">{candidate.client}</p>
          {candidate.clientTrack && (
            <p className="text-sm text-blue-600 font-medium break-words">Track: {candidate.clientTrack}</p>
          )}
          <p className="text-sm text-slate-600 break-words">{candidate.projectCity}, {candidate.projectState}</p>
          <p className="text-xs text-slate-500 break-words">{candidate.contractorStatus}</p>
        </div>
        
        <div className="space-y-1">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact Information</p>
          <p className="text-sm text-slate-900 break-all">{candidate.candidatePhone}</p>
          <p className="text-sm text-slate-600 truncate" title={candidate.candidateEmail}>{candidate.candidateEmail}</p>
          {candidate.workEmail && (
            <p className="text-xs text-slate-500 truncate" title={candidate.workEmail}>Work: {candidate.workEmail}</p>
          )}
          {candidate.linkedinUrl && (
            <a 
              href={candidate.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              LinkedIn
            </a>
          )}
        </div>
        
        <div className="space-y-1">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contract Period</p>
          <p className="text-sm text-slate-900">
            {new Date(candidate.startDate).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            })}
          </p>
          <p className="text-sm text-red-600 font-bold">
            to {new Date(candidate.sowEndDate).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            })}
          </p>
          <p className={`text-xs font-medium ${
            daysRemaining < 0 ? 'text-purple-600' :
            daysRemaining <= 7 ? 'text-red-600' : 
            daysRemaining <= 30 ? 'text-yellow-600' : 'text-blue-600'
          }`}>
            {daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue!` : 
             daysRemaining === 0 ? 'Ends today!' :
             `${daysRemaining} days remaining`}
          </p>
        </div>
        
        <div className="space-y-1">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Management</p>
          <p className="text-sm font-bold text-slate-900">{candidate.buHead}</p>
          <p className="text-sm text-slate-600">{candidate.dalName}</p>
          {candidate.reportingManagerName && (
            <p className="text-xs text-slate-500">RM: {candidate.reportingManagerName}</p>
          )}
        </div>
      </div>

      {/* Skills */}
      <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 mb-1.5">
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Primary Skills</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {candidate.primarySkills.split(',').map((skill, index) => (
            <span key={index} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[11px] rounded-full font-medium">
              {skill.trim()}
            </span>
          ))}
        </div>
      </div>

      {/* Certifications with Icons */}
{candidate.certifications && candidate.certifications.length > 0 && (
  <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-100">
    <div className="flex items-center justify-between mb-2">
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
        <Award className="w-3 h-3 text-amber-600" />
        Certifications ({candidate.certifications.length})
      </p>
    </div>
    <div className="flex flex-wrap gap-2">
      {candidate.certifications.slice(0, 3).map((cert, index) => (
        <div 
          key={index} 
          className="group relative flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-slate-50 to-slate-100 hover:from-amber-50 hover:to-yellow-50 border border-slate-200 hover:border-amber-300 rounded-lg transition-all duration-200 cursor-pointer"
          title={cert}
        >
          <CertificationIcon certName={cert} />
          <span className="text-[11px] font-medium text-slate-700 group-hover:text-amber-900 transition-colors max-w-[120px] truncate">
            {cert}
          </span>
          
          {/* Tooltip on hover */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 shadow-xl">
            {cert}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
          </div>
        </div>
      ))}
      {candidate.certifications.length > 3 && (
        <div className="flex items-center px-2.5 py-1.5 bg-slate-100 border border-slate-300 rounded-lg">
          <span className="text-[11px] font-semibold text-slate-600">
            +{candidate.certifications.length - 3} more
          </span>
        </div>
      )}
    </div>
  </div>
)}

      {/* Client Feedback */}
      {candidate.clientFeedback && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Client Feedback</p>
          <p className="text-sm text-slate-700 break-words">{candidate.clientFeedback}</p>
        </div>
      )}
    </div>
  </div>
);
          })
        )}
      </div>

      {/* Compact Pagination */}
      {totalPages > 1 && !dataLoading && (
        <div className="bg-white rounded-xl border border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-700">
              <span className="font-semibold">{startIndex + 1}</span> - <span className="font-semibold">{Math.min(endIndex, filteredRollOffCandidates.length)}</span> of <span className="font-semibold">{filteredRollOffCandidates.length}</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {(() => {
                const pages = [];
                const showPages = 5;
                let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                let endPage = Math.min(totalPages, startPage + showPages - 1);
                
                if (endPage - startPage + 1 < showPages) {
                  startPage = Math.max(1, endPage - showPages + 1);
                }
                
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(i);
                }
                
                return pages.map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white'
                        : 'text-slate-700 hover:bg-slate-50 border border-slate-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                ));
              })()}
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

<UnifiedCandidateModal
  isOpen={showUnifiedModal}
  onClose={() => {
    setShowUnifiedModal(false);
    setUnifiedModalCandidate(null);
  }}
  candidate={unifiedModalCandidate}
  onSave={handleSaveUnifiedModal}
  currentUser={currentUser}
  API_BASE_URL={API_BASE_URL}
  fetchCandidates={fetchCandidates}
/>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default RollOffTab;